/**
 * Time resolution for catalog cost rows. A cost is authored once with the
 * quarter it takes effect and holds until superseded, so a sparse table
 * answers every quarter. Two independent axes:
 *
 *   valid time       `effectiveFrom` — the quarter the cost APPLIES to
 *   transaction time `recordedAt`    — when the fact was ASSERTED
 *
 * `asOf` filters on transaction time, which is what lets a published price
 * edition reconstruct the exact basis it was derived from rather than
 * storing a copy of it.
 *
 * Returns null — never 0 — when nothing has ever been authored at or before
 * the quarter. "Never priced" and "priced at zero" are different facts, and
 * conflating them is the defect this replaces (getProductPrice returns 0 for
 * both today).
 *
 * Callers must pass rows for a single vendor; resolution does not filter on
 * `vendor` because it is implicit in the row array.
 */
import { compareQuarters } from '../constants/quarterConfig';

export interface TimedRow {
  componentSku: string;
  effectiveFrom: string;   // "2026-Q3"
  recordedAt: string;      // ISO8601
  version: number;
}

export interface Resolution<R> {
  row: R;
  source: 'authored' | 'carried';
  staleQuarters: number;
}

/** Signed quarter count from `from` to `to`. Negative when `to` is earlier. */
export function quarterDistance(from: string, to: string): number {
  const parse = (q: string) => {
    const [y, n] = q.split('-Q');
    return { year: Number(y), quarter: Number(n) };
  };
  const a = parse(from), b = parse(to);
  return (b.year - a.year) * 4 + (b.quarter - a.quarter);
}

export function resolveAt<R extends TimedRow>(
  rows: R[], componentSku: string, quarter: string, asOf?: string,
): Resolution<R> | null {
  let best: R | undefined;
  for (const r of rows) {
    if (r.componentSku !== componentSku) continue;
    if (compareQuarters(r.effectiveFrom, quarter) > 0) continue;   // not in effect yet
    if (asOf && r.recordedAt > asOf) continue;                     // not yet believed
    if (!best) { best = r; continue; }
    const byQuarter = compareQuarters(r.effectiveFrom, best.effectiveFrom);
    if (byQuarter > 0 || (byQuarter === 0 && r.version > best.version)) best = r;
  }
  if (!best) return null;
  return {
    row: best,
    source: best.effectiveFrom === quarter ? 'authored' : 'carried',
    staleQuarters: quarterDistance(best.effectiveFrom, quarter),
  };
}

export function resolveAllAt<R extends TimedRow>(
  rows: R[], quarter: string, asOf?: string,
): Map<string, Resolution<R>> {
  const out = new Map<string, Resolution<R>>();
  for (const sku of new Set(rows.map((r) => r.componentSku))) {
    const r = resolveAt(rows, sku, quarter, asOf);
    if (r) out.set(sku, r);
  }
  return out;
}
